import { Info } from 'lucide-react'
import { cn } from '@/utils/cn'

export interface ImportOutcomeCounts {
  update: number
  add: number
  needsFix: number
  rejected: number
  skipped: number
}

const SEGMENTS: {
  key: keyof ImportOutcomeCounts
  label: string
  color: string
  alwaysShow?: boolean
}[] = [
  { key: 'update', label: 'To update', color: 'bg-blue-500', alwaysShow: true },
  { key: 'add', label: 'To add', color: 'bg-green-600', alwaysShow: true },
  { key: 'needsFix', label: 'Need fixing', color: 'bg-destructive' },
  { key: 'rejected', label: "Won't import", color: 'bg-[#8A93A3]' },
  { key: 'skipped', label: 'Empty rows', color: 'bg-[#D6DAE1]' },
]

/** What happens to every row of the file. The tiles always add up to the
 * number of rows in the file. */
export function ImportOutcome({
  counts,
  showUpdateNote,
}: {
  counts: ImportOutcomeCounts
  showUpdateNote: boolean
}) {
  const total = SEGMENTS.reduce((sum, { key }) => sum + counts[key], 0)
  const shown = SEGMENTS.filter(s => s.alwaysShow || counts[s.key] > 0)

  return (
    <div className="space-y-3">
      <div
        role="img"
        aria-label={shown
          .map(s => `${counts[s.key]} ${s.label.toLowerCase()}`)
          .join(', ')}
        className="flex h-2 gap-0.5 overflow-hidden rounded-full bg-[#E5E7EB]"
      >
        {total > 0 &&
          SEGMENTS.filter(s => counts[s.key] > 0).map(s => (
            <span
              key={s.key}
              className={cn('h-full', s.color)}
              style={{ flexGrow: counts[s.key] }}
            />
          ))}
      </div>
      <dl
        className="grid gap-2"
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(110px, 1fr))`,
        }}
      >
        {shown.map(s => (
          <div
            key={s.key}
            className="flex flex-col-reverse rounded-md border border-[#E5E7EB] px-3 py-2"
          >
            <dt className="flex items-center gap-1.5 text-xs text-[#556374]">
              <span
                aria-hidden="true"
                className={cn('h-2 w-2 shrink-0 rounded-sm', s.color)}
              />
              {s.label}
            </dt>
            <dd
              className={cn(
                'text-lg font-semibold tabular-nums',
                counts[s.key] === 0 ? 'text-[#6A7282]' : 'text-[#1C1C1C]'
              )}
            >
              {counts[s.key].toLocaleString()}
            </dd>
          </div>
        ))}
      </dl>
      {showUpdateNote && (
        <p className="flex items-start gap-2 text-xs text-[#556374]">
          <Info aria-hidden="true" className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>
            Updates replace the values of every column in your file.{' '}
            <span className="font-semibold text-[#1C1C1C]">
              Blank cells clear the current value.
            </span>
          </span>
        </p>
      )}
    </div>
  )
}
